import React, { useCallback, useEffect, useState } from "react";
import DropDownTab from "../DropDownTab";
import useDropDownTab from "../DropDownTab/useDropDownTab";
import { useMyCurators } from "@src/hooks/useMyCurators";
import { SearchInput } from "../SearchInput";
import { useAuthState, useRole } from "@src/hooks/useAuth";
import ArtWorkImage from "../ArtWorkImage";
import { theme } from "@src/theme";
import styled from "styled-components";
import RenderContentStatus from "../RenderContentStatus";
import { IGuestCuratorRecord } from "@src/types/curator";
import { X } from "lucide-react";

const GuestItem = styled.div`
  display: flex;
  margin-bottom: ${theme.margins.medium};
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: auto;
  &:hover {
    background-color: ${theme.colors.opacity_yellow};
  }
  padding: 10px ${theme.margins.large};
`;
const GuestCuratorName = styled.p`
  font-weight: bold;
  color: ${theme.colors.yellow};
  margin-left: ${theme.margins.medium};
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  color: ${theme.colors.gray};
  &:hover {
    color: ${theme.colors.white};
  }
`;

type GuestDropdownProps = {
  onGuestSelect: (curatorData: IGuestCuratorRecord) => void;
  /** Optional. When provided, allows clearing the selection (for non-required fields). */
  onClear?: () => void;
  /** Optional. Controlled value: documentId of the selected guest. Enables showing initial/outside value and clear. */
  value?: string;
};
const GuestDropdown = ({
  onGuestSelect,
  onClear,
  value: valueProp,
}: GuestDropdownProps) => {
  const roleType = useRole();
  const [searchText, setSearchText] = React.useState<string>("");
  const { closeDropdown, isOpen, toggleDropdown, openDropdown } =
    useDropDownTab();
  const { curator } = useAuthState();
  const { myCurators, fetchingMyCurators } = useMyCurators(
    curator?.documentId ?? "",
    searchText,
    roleType
  );

  const [selectedItem, setSelectedItem] = useState<IGuestCuratorRecord | null>(
    null
  );

  // Sync selectedItem with controlled value (e.g. from form reset or filters)
  useEffect(() => {
    if (valueProp === undefined) return;
    if (!valueProp || valueProp.trim() === "") {
      setSelectedItem(null);
      return;
    }
    const found = myCurators?.find((c) => c.documentId === valueProp);
    setSelectedItem(found ?? null);
  }, [valueProp, myCurators]);

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);
    },
    []
  );

  const onGuestSelectAction = useCallback(
    (curatorData: IGuestCuratorRecord) => {
      onGuestSelect(curatorData);
      setSelectedItem(curatorData);
      closeDropdown();
    },
    [onGuestSelect, closeDropdown]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedItem(null);
      onClear?.();
      closeDropdown();
    },
    [onClear, closeDropdown]
  );

  const displayTitle =
    selectedItem?.name ||
    (valueProp && myCurators?.find((c) => c.documentId === valueProp)?.name) ||
    "Guest";
  const hasSelection = !!(selectedItem || (valueProp && valueProp.trim()));

  return (
    <DropDownTab
      contentContainerStyles={{
        padding: 0,
      }}
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
      openDropdown={openDropdown}
      title={displayTitle}
      titleStyles={{
        color: hasSelection ? theme.colors.white : theme.colors.gray,
      }}
      secondaryTitleComponent={
        hasSelection && onClear ? (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label="Clear guest selection"
          >
            <X size={16} />
          </ClearButton>
        ) : undefined
      }
      key={1}
    >
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div style={{ padding: `${theme.margins.large}` }}>
          <SearchInput
            style={{ marginBottom: theme.margins.medium }}
            placeholder="Search for guests"
            onChange={handleTextChange}
          />
          <RenderContentStatus
            loading={fetchingMyCurators}
            noResultsFoundMessage="No guests found"
            results={myCurators?.length}
          />
        </div>
        {myCurators.map((curatorItem) => (
          <GuestItem
            role="button"
            onClick={() => onGuestSelectAction(curatorItem)}
            style={{ display: "flex", marginBottom: theme.margins.small }}
            key={curatorItem.id}
          >
            <ArtWorkImage
              ImgStyle={{
                borderRadius: 0,
              }}
              value={
                curatorItem?.shareCover?.formats?.thumbnail?.url ??
                curatorItem?.shareCover?.url
              }
              customSize="55px"
            />
            <GuestCuratorName>{curatorItem.name}</GuestCuratorName>
          </GuestItem>
        ))}
      </div>
    </DropDownTab>
  );
};

export default GuestDropdown;
